import { useEffect, useState, type FormEvent } from 'react';
import {
  TEAM_ALERT_MAX_RECIPIENTS,
  TEAM_DAILY_REPORTS,
  TEAM_INSTANT_ALERTS,
  channelLabel,
  type TeamAlertRecipient,
  type TeamAlertTemplate,
  type TeamDailyReport,
  type TeamInstantAlert,
} from '@neuroedge/shared';
import { Button, Card, Modal, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import { useChannels } from '../../api/channels';
import { useAssignableMembers } from '../../api/users';
import {
  useAddTeamAlertRecipient,
  useRemoveTeamAlertRecipient,
  useSendTeamAlertTest,
  useSetUpTeamAlertTemplates,
  useTeamAlerts,
  useUpdateTeamAlertRecipient,
  useUpdateTeamAlertSettings,
} from '../../api/team-alerts';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const STATUS_LABEL: Record<TeamAlertRecipient['status'], string> = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  OPTED_OUT: 'Replied STOP',
};

const TEMPLATE_STATUS: Record<TeamAlertTemplate['status'], { label: string; color?: string }> = {
  MISSING: { label: 'Not set up' },
  PENDING: { label: 'Waiting for Meta', color: 'var(--color-accent-amber)' },
  APPROVED: { label: 'Approved', color: 'var(--color-success)' },
  REJECTED: { label: 'Rejected', color: 'var(--color-danger)' },
  PAUSED: { label: 'Paused by Meta', color: 'var(--color-danger)' },
  DISABLED: { label: 'Disabled by Meta', color: 'var(--color-danger)' },
};

const ALERT_LABEL: Record<string, string> = Object.fromEntries([
  ['ne_team_welcome', 'Welcome / test'],
  ['ne_team_new_lead', 'New lead'],
  ['ne_team_appointment', 'Appointment'],
  ['ne_team_order', 'Order'],
  ['ne_team_quotation', 'Quotation request'],
  ['ne_team_morning_summary', 'Morning summary'],
  ['ne_team_overdue', 'Overdue reminder'],
  ['ne_team_day_results', 'End-of-day results'],
]);

/** "919876543210" -> "+91 98765 43210"-ish, just for reading. */
const showPhone = (digits: string) => `+${digits}`;

/**
 * Settings > WhatsApp alerts for my team (ADR-039): the workspace's own
 * WhatsApp number sends quick updates (new leads, bookings, orders,
 * quotation requests) and daily reports to the team numbers added here -
 * each with the admin's confirmation that the person agreed.
 */
export function TeamAlertsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('settings.manage');
  const overview = useTeamAlerts();
  const channels = useChannels({ type: 'WHATSAPP' });
  const toast = useToast();
  const [editing, setEditing] = useState<TeamAlertRecipient | 'new' | null>(null);
  const update = useUpdateTeamAlertRecipient();
  const remove = useRemoveTeamAlertRecipient();
  const test = useSendTeamAlertTest();

  if (overview.isPending) return <PageState kind="loading" message="Loading team alerts…" />;
  if (overview.isError)
    return (
      <PageState
        kind="error"
        message={overview.error.message}
        onRetry={() => void overview.refetch()}
      />
    );
  const data = overview.data;
  const full = data.recipients.length >= TEAM_ALERT_MAX_RECIPIENTS;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 900 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>WhatsApp alerts for my team</h1>
        <p style={{ ...muted, margin: 'var(--space-2) 0 0' }}>
          Your WhatsApp number sends your team quick updates - new leads, bookings, orders - and a
          daily report, so nobody has to open the dashboard to know what needs doing. Messages use
          templates Meta approves; Meta charges your WhatsApp account a small fee per message.
        </p>
      </div>

      <SendingCard
        canManage={canManage}
        settings={data.settings}
        channels={(channels.data ?? []).map((c) => ({ value: c.id, label: channelLabel(c) }))}
      />

      <TemplatesCard
        canManage={canManage}
        templates={data.templates}
        hasChannel={!!data.settings.channelId}
      />

      <Card title="Team numbers">
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {data.recipients.length === 0 ? (
            <p style={{ ...muted, margin: 0 }}>
              No numbers yet. Add the WhatsApp numbers of the people who should get updates.
            </p>
          ) : (
            data.recipients.map((r) => (
              <div
                key={r.id}
                role="group"
                aria-label={r.name}
                style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  display: 'grid',
                  gap: 'var(--space-1)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <strong>{r.name}</strong>
                  <span style={muted}>{showPhone(r.phone)}</span>
                  <span
                    className="ne-badge"
                    style={
                      r.status === 'ACTIVE'
                        ? { borderColor: 'var(--color-success)', color: 'var(--color-success)' }
                        : { borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }
                    }
                  >
                    {STATUS_LABEL[r.status]}
                  </span>
                  <span style={{ flex: 1 }} />
                  {canManage ? (
                    <>
                      <Button variant="secondary" onClick={() => setEditing(r)}>
                        Edit
                      </Button>
                      {r.status === 'ACTIVE' ? (
                        <Button
                          variant="secondary"
                          disabled={update.isPending}
                          onClick={() =>
                            update.mutate(
                              { id: r.id, input: { status: 'PAUSED' } },
                              { onSuccess: () => toast.success(`Alerts to ${r.name} paused.`) },
                            )
                          }
                        >
                          Pause
                        </Button>
                      ) : r.status === 'PAUSED' ? (
                        <Button
                          variant="secondary"
                          disabled={update.isPending}
                          onClick={() =>
                            update.mutate(
                              { id: r.id, input: { status: 'ACTIVE' } },
                              { onSuccess: () => toast.success(`Alerts to ${r.name} resumed.`) },
                            )
                          }
                        >
                          Resume
                        </Button>
                      ) : null}
                      <Button
                        variant="secondary"
                        disabled={test.isPending || r.status !== 'ACTIVE'}
                        onClick={() =>
                          test.mutate(r.id, {
                            onSuccess: (d) =>
                              d.status === 'SENT'
                                ? toast.success(`Test message sent to ${r.name}.`)
                                : toast.error(d.error ?? 'The test message was not sent.'),
                            onError: (err) => toast.error(err.message),
                          })
                        }
                      >
                        Send test
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            !window.confirm(`Stop all alerts to ${r.name} and remove the number?`)
                          )
                            return;
                          remove.mutate(r.id, {
                            onSuccess: () => toast.success('Number removed.'),
                          });
                        }}
                      >
                        Remove
                      </Button>
                    </>
                  ) : null}
                </div>
                <span style={muted}>
                  {[
                    ...r.instantAlerts.map(
                      (k) => TEAM_INSTANT_ALERTS.find((a) => a.key === k)?.label ?? k,
                    ),
                    ...r.dailyReports.map(
                      (k) => TEAM_DAILY_REPORTS.find((a) => a.key === k)?.label ?? k,
                    ),
                  ].join(' · ') || 'Nothing chosen yet'}
                  {r.quietFrom ? ` · Quiet ${r.quietFrom}–${r.quietTo}` : ''}
                </span>
                <span style={{ ...muted, fontSize: '0.75rem' }}>
                  {r.status === 'OPTED_OUT'
                    ? `Replied STOP on ${new Date(r.optedOutAt ?? r.createdAt).toLocaleDateString()} - edit to turn alerts back on once they agree.`
                    : `Agreement confirmed by ${r.consentConfirmedBy ?? 'an admin'} on ${new Date(r.consentConfirmedAt).toLocaleDateString()}.`}
                </span>
              </div>
            ))
          )}
          {canManage ? (
            <div>
              <Button disabled={full} onClick={() => setEditing('new')}>
                + Add number
              </Button>
              {full ? (
                <span style={{ ...muted, marginLeft: 'var(--space-2)' }}>
                  Up to {TEAM_ALERT_MAX_RECIPIENTS} numbers.
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      <Card title="Recent messages">
        {data.deliveries.length === 0 ? (
          <p style={{ ...muted, margin: 0 }}>Nothing sent yet.</p>
        ) : (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
          >
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th>When</th>
                <th>To</th>
                <th>Message</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.deliveries.map((d) => (
                <tr key={d.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td>{new Date(d.sentAt ?? d.createdAt).toLocaleString()}</td>
                  <td>{d.recipientName}</td>
                  <td>{ALERT_LABEL[d.alertType] ?? d.alertType}</td>
                  <td>
                    {d.status === 'SENT'
                      ? 'Sent'
                      : d.status === 'QUEUED'
                        ? 'Waiting'
                        : d.status === 'FAILED'
                          ? 'Failed'
                          : 'Not sent'}
                    {d.error ? <div style={muted}>{d.error}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {editing ? (
        <RecipientModal
          recipient={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function SendingCard({
  canManage,
  settings,
  channels,
}: {
  canManage: boolean;
  settings: { channelId: string | null; enabled: boolean; dailyReportTime: string };
  channels: { value: string; label: string }[];
}) {
  const save = useUpdateTeamAlertSettings();
  const toast = useToast();
  const [channelId, setChannelId] = useState(settings.channelId ?? '');
  const [enabled, setEnabled] = useState(settings.enabled);
  const [time, setTime] = useState(settings.dailyReportTime);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setChannelId(settings.channelId ?? '');
    setEnabled(settings.enabled);
    setTime(settings.dailyReportTime);
  }, [settings.channelId, settings.enabled, settings.dailyReportTime]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!channelId) found.channelId = 'Choose the WhatsApp number that sends alerts.';
    if (!TIME_RE.test(time)) found.dailyReportTime = 'Enter a time like 09:00.';
    setErrors(found);
    if (Object.keys(found).length) return;
    try {
      await save.mutateAsync({ channelId, enabled, dailyReportTime: time });
      toast.success('Saved.');
    } catch (err) {
      const fields = fieldErrorsFromApiError(err);
      if (Object.keys(fields).length) setErrors(fields);
      else toast.error(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <Card title="Sending">
      <form className="ne-auth__form" onSubmit={(e) => void onSubmit(e)} noValidate>
        <Select
          label="Send from"
          value={channelId}
          disabled={!canManage}
          onChange={(e) => {
            setChannelId(e.target.value);
            setErrors(({ channelId: _gone, ...rest }) => rest);
          }}
          options={[
            {
              value: '',
              label: channels.length
                ? 'Choose a WhatsApp number…'
                : 'Connect a WhatsApp number first',
            },
            ...channels,
          ]}
          error={errors.channelId}
        />
        <TextField
          label="Daily reports at"
          type="time"
          value={time}
          disabled={!canManage}
          onChange={(e) => {
            setTime(e.target.value);
            setErrors(({ dailyReportTime: _gone, ...rest }) => rest);
          }}
          error={errors.dailyReportTime}
        />
        <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={enabled}
            disabled={!canManage}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Send team alerts
        </label>
        {canManage ? (
          <div>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        ) : null}
      </form>
    </Card>
  );
}

function TemplatesCard({
  canManage,
  templates,
  hasChannel,
}: {
  canManage: boolean;
  templates: TeamAlertTemplate[];
  hasChannel: boolean;
}) {
  const setUp = useSetUpTeamAlertTemplates();
  const toast = useToast();
  const missing = templates.filter((t) => t.status === 'MISSING').length;
  const marketing = templates.filter((t) => t.category === 'MARKETING');
  return (
    <Card title="Message templates">
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ ...muted, margin: 0 }}>
          WhatsApp only lets a business start a conversation with a template Meta has approved. Set
          them up once - Meta usually reviews them within a day. Each kind of alert starts going out
          as soon as its template is approved.
        </p>
        {!hasChannel ? (
          <p style={{ ...muted, margin: 0 }}>Choose the number that sends alerts first.</p>
        ) : (
          <>
            <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', display: 'grid', gap: 2 }}>
              {templates.map((t) => (
                <li key={t.name}>
                  {t.label}:{' '}
                  <span style={{ color: TEMPLATE_STATUS[t.status].color }}>
                    {TEMPLATE_STATUS[t.status].label}
                  </span>
                </li>
              ))}
            </ul>
            {marketing.length ? (
              <p style={{ ...muted, margin: 0, color: 'var(--color-accent-amber)' }}>
                Meta filed {marketing.map((t) => `"${t.label}"`).join(', ')} as marketing, which
                costs more per message.
              </p>
            ) : null}
            {canManage && missing > 0 ? (
              <div>
                <Button
                  disabled={setUp.isPending}
                  onClick={() =>
                    setUp.mutate(undefined, {
                      onSuccess: (r) =>
                        r.failed.length
                          ? toast.error(
                              `${r.failed.length} could not be submitted: ${r.failed[0]!.error}`,
                            )
                          : toast.success(`${r.created.length} templates sent to Meta for review.`),
                      onError: (err) => toast.error(err.message),
                    })
                  }
                >
                  {setUp.isPending ? 'Sending to Meta…' : 'Set up alert templates'}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
}

function RecipientModal({
  recipient,
  onClose,
}: {
  recipient: TeamAlertRecipient | null;
  onClose: () => void;
}) {
  const add = useAddTeamAlertRecipient();
  const update = useUpdateTeamAlertRecipient();
  const members = useAssignableMembers();
  const toast = useToast();
  const [name, setName] = useState(recipient?.name ?? '');
  const [phone, setPhone] = useState(recipient ? showPhone(recipient.phone) : '');
  const [memberId, setMemberId] = useState(recipient?.tenantUserId ?? '');
  const [instant, setInstant] = useState<TeamInstantAlert[]>(
    recipient?.instantAlerts ?? ['NEW_LEAD', 'APPOINTMENT', 'ORDER', 'QUOTATION'],
  );
  const [daily, setDaily] = useState<TeamDailyReport[]>(
    recipient?.dailyReports ?? ['MORNING_SUMMARY'],
  );
  const [quiet, setQuiet] = useState(Boolean(recipient?.quietFrom));
  const [quietFrom, setQuietFrom] = useState(recipient?.quietFrom ?? '21:00');
  const [quietTo, setQuietTo] = useState(recipient?.quietTo ?? '08:00');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const digits = phone.replace(/\D/g, '');
  const numberChanged = recipient !== null && digits !== recipient.phone;
  const optedOut = recipient?.status === 'OPTED_OUT';
  const needsConsent = recipient === null || numberChanged || optedOut;
  const clear = (k: string) => setErrors(({ [k]: _gone, ...rest }) => rest);

  function toggle<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Enter a name.';
    else if (name.trim().length > 120) e.name = 'Name must be 120 characters or fewer.';
    if (!/^[1-9]\d{7,14}$/.test(digits) || !phone.trim().startsWith('+')) {
      e.phone = 'Enter the number with its country code, e.g. +91 98765 43210.';
    }
    if (quiet) {
      if (!TIME_RE.test(quietFrom)) e.quietFrom = 'Enter a time like 21:00.';
      if (!TIME_RE.test(quietTo)) e.quietTo = 'Enter a time like 08:00.';
    }
    if (needsConsent && !consent) {
      e.consentConfirmed = 'Confirm this person agreed to receive WhatsApp alerts.';
    }
    return e;
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      setError('Please fix the highlighted fields.');
      return;
    }
    const fields = {
      name: name.trim(),
      phone: `+${digits}`,
      tenantUserId: memberId || null,
      instantAlerts: instant,
      dailyReports: daily,
      quietFrom: quiet ? quietFrom : null,
      quietTo: quiet ? quietTo : null,
    };
    try {
      if (recipient) {
        await update.mutateAsync({
          id: recipient.id,
          input: {
            ...fields,
            ...(needsConsent ? { consentConfirmed: true } : {}),
            ...(optedOut ? { status: 'ACTIVE' as const } : {}),
          },
        });
        toast.success('Saved.');
      } else {
        await add.mutateAsync({ ...fields, consentConfirmed: true });
        toast.success(`${fields.name} added - they get a short welcome message.`);
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const server = fieldErrorsFromApiError(err);
        if (Object.keys(server).length) {
          setErrors(server);
          setError('Please fix the highlighted fields.');
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  const busy = add.isPending || update.isPending;
  return (
    <Modal title={recipient ? `Edit ${recipient.name}` : 'Add a team number'} onClose={onClose}>
      <form className="ne-auth__form" onSubmit={(e) => void onSubmit(e)} noValidate>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clear('name');
          }}
          error={errors.name}
        />
        <TextField
          label="WhatsApp number"
          inputMode="tel"
          placeholder="+91 98765 43210"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            clear('phone');
          }}
          error={errors.phone}
        />
        <Select
          label="Team member (optional)"
          value={memberId}
          onChange={(e) => setMemberId(e.target.value)}
          options={[
            { value: '', label: 'Not linked' },
            ...(members.data ?? []).map((m) => ({ value: m.tenantUserId, label: m.displayName })),
          ]}
          error={errors.tenantUserId}
        />

        <fieldset
          style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-1)' }}
        >
          <legend className="ne-field__label">Instant alerts</legend>
          {TEAM_INSTANT_ALERTS.map((a) => (
            <label
              key={a.key}
              style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'start' }}
            >
              <input
                type="checkbox"
                aria-label={a.label}
                checked={instant.includes(a.key)}
                onChange={() => setInstant((l) => toggle(l, a.key))}
              />
              <span>
                {a.label}
                <span style={{ ...muted, display: 'block' }}>{a.description}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <fieldset
          style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-1)' }}
        >
          <legend className="ne-field__label">Daily reports</legend>
          {TEAM_DAILY_REPORTS.map((r) => (
            <label
              key={r.key}
              style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'start' }}
            >
              <input
                type="checkbox"
                aria-label={r.label}
                checked={daily.includes(r.key)}
                onChange={() => setDaily((l) => toggle(l, r.key))}
              />
              <span>
                {r.label}
                <span style={{ ...muted, display: 'block' }}>{r.description}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <input type="checkbox" checked={quiet} onChange={(e) => setQuiet(e.target.checked)} />
          Quiet hours - hold instant alerts and send them afterwards
        </label>
        {quiet ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <TextField
              label="From"
              type="time"
              value={quietFrom}
              onChange={(e) => {
                setQuietFrom(e.target.value);
                clear('quietFrom');
              }}
              error={errors.quietFrom}
            />
            <TextField
              label="To"
              type="time"
              value={quietTo}
              onChange={(e) => {
                setQuietTo(e.target.value);
                clear('quietTo');
              }}
              error={errors.quietTo}
            />
          </div>
        ) : null}

        {needsConsent ? (
          <div className="ne-field">
            <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'start' }}>
              <input
                type="checkbox"
                checked={consent}
                aria-invalid={errors.consentConfirmed ? true : undefined}
                onChange={(e) => {
                  setConsent(e.target.checked);
                  clear('consentConfirmed');
                }}
              />
              <span>
                {optedOut
                  ? 'This person replied STOP earlier and has now agreed to receive WhatsApp alerts again.'
                  : 'This person agreed to receive WhatsApp alerts from us.'}
              </span>
            </label>
            {errors.consentConfirmed ? (
              <span className="ne-field__error" role="alert">
                {errors.consentConfirmed}
              </span>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <p className="ne-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : recipient ? 'Save' : 'Add number'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
